import { initializeApp } from 'firebase/app'
import {
  isSupported,
  getMessaging,
  getToken,
  deleteToken as firebaseDeleteToken,
  onMessage
} from 'firebase/messaging'

let globalFirebaseApp
let globalMessaging

let firebaseServiceWorkerConfigurated = false

export const resetFirebaseServiceWorkerConfigurated = () => {
  firebaseServiceWorkerConfigurated = false
}

// Test helpers to reset global state
export const resetGlobalFirebaseApp = () => {
  globalFirebaseApp = undefined
}

export const resetGlobalMessaging = () => {
  globalMessaging = undefined
}

export const getFirebaseApp = async (firebaseConfig = {}) => {
  if (globalFirebaseApp) {
    return globalFirebaseApp
  }

  if (await isSupported()) {
    globalFirebaseApp = initializeApp(firebaseConfig)
  }

  return globalFirebaseApp
}

export const getFirebaseMessaging = async (firebaseConfig = {}) => {
  if (globalMessaging) {
    return globalMessaging
  }

  const app = await getFirebaseApp(firebaseConfig)
  if (app && (await isSupported())) {
    globalMessaging = getMessaging(app)
  }

  return globalMessaging
}

export const SERVICE_WORKER_FILENAME = '/firebase-messaging-sw.js'

export const SERVICE_WORKERS_EVENTS = {
  initializeFirebase: 'initializeFirebase'
}

export const NOTIFICATION_GRANTED_PERMISSION = 'granted'

export const isBrowserWebPushPermissionGranted = () =>
  // eslint-disable-next-line implicit-arrow-linebreak
  Notification.permission === NOTIFICATION_GRANTED_PERMISSION

export const postMessageToWorker = (worker, message) => {
  console.debug(
    `[CDN DEBUG] Sending message to CDN ServiceWorker = ${JSON.stringify(
      message,
      null,
      2
    )}`
  )

  return worker.postMessage && worker?.postMessage(message)
}

export const postFirebaseConfigToServiceWorker = (worker, firebaseConfig) => {
  if (!worker) return null

  const message = {
    type: SERVICE_WORKERS_EVENTS.initializeFirebase,
    firebaseConfig
  }

  return postMessageToWorker(worker, message)
}

export const firebaseServiceWorkerRegistration = async (
  registration,
  firebaseConfig
) => {
  if (registration) {
    const worker = registration.active

    if (await isSupported()) {
      await getFirebaseMessaging(firebaseConfig)
      firebaseServiceWorkerConfigurated = true
    }

    postFirebaseConfigToServiceWorker(worker, firebaseConfig)
  }
}

export const handleAskToPermissionError = (error) => {
  console.log('Unable to get permission to notify.', error)
}

export const askToPermission = async (messaging, vapidKey) => {
  if (!messaging) return false

  try {
    const permission = await Notification.requestPermission()
    if (permission === 'granted') {
      const token = await getToken(messaging, { vapidKey })
      return token
    }
    return false
  } catch (err) {
    handleAskToPermissionError(err)
    return false
  }
}

export const messageListener = (cb) => {
  const { serviceWorker } = window.navigator || {}

  if (!serviceWorker) return false

  return serviceWorker.addEventListener('message', cb)
}

export const initialize = async (firebaseConfig = {}) => {
  // eslint-disable-next-line no-new
  try {
    const messaging = await getFirebaseMessaging(firebaseConfig)
    const vapidKey = firebaseConfig.publicVapidKey

    messageListener((message) => console.log(message)) // eslint-disable-line

    const token = await askToPermission(messaging, vapidKey)
    return token
  } catch (error) {
    throw error
  }
}

export const getUserNotificationToken = async (firebaseConfig = {}) => {
  // If user doesn't gave permission, there's no point in trying to get user's token
  if (!isBrowserWebPushPermissionGranted()) {
    return null
  }

  const messaging = await getFirebaseMessaging(firebaseConfig)
  const vapidKey = firebaseConfig.publicVapidKey

  const token = await askToPermission(messaging, vapidKey)
  return token
}

export const deleteToken = async (token = '', firebaseConfig = {}) => {
  const messaging = await getFirebaseMessaging(firebaseConfig)

  if (!messaging) {
    throw new Error('Messaging not available')
  }

  try {
    const success = await firebaseDeleteToken(messaging)
    if (!success) {
      throw new Error('Failed to delete token')
    }
    return true
  } catch (error) {
    throw error
  }
}

export const registerServiceWorker = async (
  firebaseConfig,
  serviceWorkerFilename = SERVICE_WORKER_FILENAME
) => {
  const { serviceWorker } = window.navigator || {}

  if (!serviceWorker) {
    throw new Error('Service Worker not supported')
  }

  const registration = await serviceWorker.register(serviceWorkerFilename, {
    scope: '/'
  })

  await firebaseServiceWorkerRegistration(registration, firebaseConfig)

  // If for some reason the user already gave us permission, but it's token was invalidated
  // we try to send it again to the remote API
  try {
    const token = await getUserNotificationToken(firebaseConfig)
    return token
  } catch (error) {
    // If user don't gave notifications permission just return null
    return null
  }
}
